/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome'
import { useFocusEffect } from 'expo-router'
import { ComponentProps, CSSProperties, ReactNode, useCallback, useRef, useState } from 'react'
import { colorWithBrightness, useTheme } from '../../hooks/useTheme'
import { ActivityIndicator } from '../ActivityIndicator'

type Props = {
  columns: {
    data: string
    title: string
    width?: number | string
    alignRight?: boolean
    style?: CSSProperties
    sortable?: boolean
    hidden?: boolean
    renderer?: (row: number, value: any) => ReactNode
  }[]
  data: any[]
  loading?: boolean
  noDataMessage?: string
  noDataButton?: ReactNode
  orderBy?: string
  sortOrder?: 'asc' | 'desc'
  compact?: boolean
  minHeight?: number
  fixedLayout?: boolean
  stickyHeader?: boolean
  scrollHorizontally?: boolean
  borderRadius?: number
  rowClasses?: (row: number) => string[]
  onRowClick?: (row: any) => void
  onRowCheck?: (row: any, checked: boolean) => void
  onHeaderCheck?: (rows: any[], checked: boolean) => void
  onUpdateSort?: ({ orderBy, sortOrder }: { orderBy: string, sortOrder: 'asc' | 'desc' }) => void
}

export function Table({
  columns,
  data,
  loading,
  noDataMessage,
  noDataButton,
  orderBy,
  sortOrder,
  compact,
  minHeight,
  fixedLayout,
  stickyHeader,
  scrollHorizontally = true,
  borderRadius,
  rowClasses,
  onRowClick,
  onRowCheck,
  onHeaderCheck,
  onUpdateSort,
}: Props) {
  const theme = useTheme()

  const [rowsChecked, setRowsChecked] = useState<Record<number, boolean>>(() =>
    Object.fromEntries(data.map((_, index) => [index, false]))
  )

  const filteredColumns = columns.filter((c) => !c.hidden)

  const tableRef = useRef<HTMLTableElement>(null)

  useFocusEffect(
    useCallback(() => {
      if (!stickyHeader) return
      if (!tableRef.current) return
      if (window.innerWidth <= theme.breakpoints.l) return

      const headerRow = tableRef.current.querySelector('thead tr') as HTMLTableRowElement
      let rafId = 0

      function handleScroll() {
        cancelAnimationFrame(rafId)
        rafId = requestAnimationFrame(() => {
          const tableRect = tableRef.current!.getBoundingClientRect()
          if (tableRect.top < 0) {
            headerRow.style.transform = `translateY(${-tableRect.top}px)`
          } else {
            headerRow.style.transform = 'none'
          }
        })
      }

      window.addEventListener('scroll', handleScroll, { passive: true })

      return () => {
        window.removeEventListener('scroll', handleScroll)
        cancelAnimationFrame(rafId)
        headerRow.style.transform = 'none'
      }
    }, [theme, stickyHeader])
  )

  const styles = css({
    width: '100%',
    tableLayout: fixedLayout ? 'fixed' : undefined,
    borderCollapse: 'collapse',
    'thead': {
      'tr': {
        willChange: stickyHeader ? 'transform' : undefined,
      },
      'th': {
        fontFamily: theme.fonts.body[600],
        fontWeight: 'normal',
        fontSize: compact ? 13.5 : 14,
        textAlign: 'left',
        padding: compact ? '12px 14px' : '15px 18px',
        background: theme.colors.headerBackground,
        borderBottom: `1px solid ${theme.colors.panelBorder}`,
        position: 'relative',
        userSelect: 'none',
        whiteSpace: 'nowrap',
        '&:first-child': {
          borderTopLeftRadius: borderRadius,
        },
        '&:last-child': {
          borderTopRightRadius: borderRadius,
        },
        '&.sortActive': {
          '&:after': {
            content: '"\\2303"',
            display: 'inline-block',
            lineHeight: 1,
            fontSize: 16,
            fontFamily: theme.fonts.body[400],
            fontWeight: 'normal',
            marginLeft: 6,
            transform: 'translateY(4px)',
          },
          '&.desc:after': {
            transform: 'rotate(180deg) translateY(3px)',
          },
        },
      },
    },
    'tbody': {
      'tr': {
        borderTop: `1px solid ${theme.colors.panelBorder}`,
        '&.clickable': {
          cursor: 'pointer',
          ':hover': {
            background: colorWithBrightness(theme.colors.surface, -5),
          },
        },
      },
      'td': {
        color: theme.colors.foreground,
        fontFamily: theme.fonts.body[400],
        fontSize: compact ? 13.5 : 14,
        lineHeight: 1.35,
        padding: compact ? '12px 14px' : '15px 18px',
        '&.loading, &.noData': {
          height: minHeight ?? (compact ? 120 : 250),
          '> div': {
            position: 'sticky',
            left: '50%',
            transform: 'translateX(-50%)',
            width: 'fit-content',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            rowGap: theme.spacing,
            '> .text': {
              fontSize: compact ? 14.5 : 15,
              textAlign: 'center',
              opacity: 0.7,
            },
          },
        },
      },
    },
  })

  return (
    <div css={{ overflowX: scrollHorizontally ? 'auto' : undefined }}>
      <table ref={tableRef} css={styles}>
        <thead>
          <tr>
            {onRowCheck && (
              <th>
                {onHeaderCheck && (
                  <Checkbox
                    checked={Object.values(rowsChecked).every((checked) => checked)}
                    indeterminate={Object.values(rowsChecked).filter((checked) => checked).length > 0}
                    compact={!!compact}
                    onChange={() => {
                      const someChecked = Object.values(rowsChecked).filter((checked) => checked).length > 0
                      setRowsChecked(Object.fromEntries(data.map((_, index) => [index, !someChecked])))
                      onHeaderCheck(data, !someChecked)
                    }}
                  />
                )}
              </th>
            )}
            {filteredColumns.map((col) => (
              <th
                key={col.data}
                className={orderBy === col.data ? `sortActive ${sortOrder}` : undefined}
                style={{
                  width: col.width,
                  textAlign: col.alignRight ? 'right' : undefined,
                  cursor: col.sortable ? 'pointer' : undefined,
                }}
                onClick={(onUpdateSort && col.sortable) ?
                  () => onUpdateSort({
                    orderBy: col.data,
                    sortOrder: orderBy !== col.data ? 'asc' : sortOrder === 'asc' ? 'desc' : 'asc',
                  }) : undefined}
              >{col.title}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={filteredColumns.length} className='loading'>
                <div>
                  <ActivityIndicator style={{ marginHorizontal: 'auto' }} />
                </div>
              </td>
            </tr>
          ) : data.length > 0 ? (
            data.map((row, index) => {
              const classes = rowClasses ? rowClasses(index) : []
              const clickable = onRowClick && !row.disabled
              if (clickable) classes.push('clickable')

              return (
                <tr
                  key={index}
                  className={classes.join(' ')}
                  onClick={clickable ? () => onRowClick(row) : undefined}
                >
                  {onRowCheck && (
                    <td>
                      <Checkbox
                        checked={rowsChecked[index]}
                        compact={!!compact}
                        onChange={(e) => {
                          setRowsChecked({
                            ...rowsChecked,
                            [index]: e.target.checked,
                          })
                          onRowCheck(row, e.target.checked)
                        }}
                      />
                    </td>
                  )}
                  {filteredColumns.map((col) => (
                    <td key={col.data} style={{ textAlign: col.alignRight ? 'right' : 'left', ...col.style }}>
                      {col.renderer ? col.renderer(index, row[col.data]) : row[col.data]}
                    </td>
                  ))}
                </tr>
              )
            })
          ) : (
            <tr>
              <td colSpan={filteredColumns.length} className='noData'>
                <div>
                  <div className='text'>{noDataMessage ?? 'No data'}</div>
                  {noDataButton}
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

function Checkbox({ checked, indeterminate, compact, ...props }: ComponentProps<'input'> & {
  indeterminate?: boolean
  compact: boolean
}) {
  const theme = useTheme()

  const styles = css({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: compact ? -14 : -18,
    position: 'relative',
    'input[type=checkbox]': {
      appearance: 'none',
      width: compact ? 14.5 : 15,
      height: compact ? 14.5 : 15,
      background: theme.colors.surface,
      border: `1px solid ${theme.input.border}`,
      borderRadius: 3,
      margin: 0,
      boxShadow: theme.web.shadow.input,
      cursor: 'pointer',
      ':hover': {
        borderColor: theme.input.borderHover,
      },
      ':focus-visible': {
        borderColor: theme.colors.primary,
      },
      ':checked': {
        background: theme.colors.primary,
        borderColor: theme.colors.primary,
      },
    },
    '.icon': {
      position: 'absolute',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      pointerEvents: 'none',
    },
  })

  return (
    <div css={styles}>
      <input {...props} checked={!!checked || !!indeterminate} type='checkbox' />
      {(checked || indeterminate) && (
        <div className='icon'>
          <FontAwesomeIcon
            icon={['fas', checked ? 'check' : 'minus']}
            size={11.5}
            color='#FFFFFF'
          />
        </div>
      )}
    </div>
  )
}
